import { useCallback } from 'react';
import { format } from 'date-fns';
import { LoadingAppViewModal } from 'design-system/LoadingAppView';
import { selectNetworth, selectAnnualIncome, selectFundingSource, selectEmploymentStatus, selectShowMultiAccountTradingView, selectHideFundingAccountAndAutoinvestFromInvestOnboarding, } from 'features/invest/reducers/selectors';
import Sentry from 'utils/sentry';
import { postAdditionalUserInfo } from 'actions';
import * as actions from 'features/invest/actions';
import useFetch, { muteAll } from 'hooks/useFetch';
import { setConnectedAccount } from 'features/invest/actions';
import { postFunnelEvents } from 'features/funnel-events/actions';
import { InvestFunnelEvents } from 'features/funnel-events/types';
import { DOB_STORAGE_FORMAT } from 'hooks/useDateTimePickerDOB';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { useHasSourceOfFundsInfo } from 'features/invest/hooks/useHasSourceOfFundsInfo';
import { blockInvestOnboardingOnWeb } from 'features/invest/webGate';
const broker = 'seccl';
export const useCreateInvestAccount = () => {
    const store = useAppStore();
    const hasSourceOfFundsInfo = useHasSourceOfFundsInfo();
    const navigation = useMainStackNavigation();
    const annualIncome = useAppSelector(selectAnnualIncome);
    const networth = useAppSelector(selectNetworth);
    const fundingSource = useAppSelector(selectFundingSource);
    const employmentStatus = useAppSelector(selectEmploymentStatus);
    const hideFundingAccountAndAutoinvestFlow = useAppSelector(selectHideFundingAccountAndAutoinvestFromInvestOnboarding);
    const [, , , fetch] = useFetch(muteAll);
    const dispatch = useAppDispatch();
    const submit = useCallback(async (fundingAccountToSet) => {
        if (blockInvestOnboardingOnWeb())
            return;
        const onboardingState = store.getState().invest.onboarding;
        const isMigrationToSecclFlow = selectShowMultiAccountTradingView(store.getState());
        const { accountType } = onboardingState || {};
        if (!accountType || !onboardingState) {
            return;
        }
        try {
            LoadingAppViewModal.show({ title: 'Creating account, please wait...' });
            if (!isMigrationToSecclFlow) {
                const { jobTitle, employerName, employerAddress, isAffiliatedExchangeOrFinra, isControlPerson, isPoliticallyExposed, } = onboardingState?.params || {};
                if (annualIncome &&
                    networth &&
                    fundingSource &&
                    employmentStatus &&
                    (broker === 'seccl' ||
                        (typeof isAffiliatedExchangeOrFinra === 'boolean' &&
                            typeof isControlPerson === 'boolean' &&
                            typeof isPoliticallyExposed === 'boolean'))) {
                    const { annualIncomeMin, annualIncomeMax } = annualIncome;
                    const { liquidNetWorthMin, liquidNetWorthMax } = networth;
                    if (!hasSourceOfFundsInfo) {
                        const userInfoResponse = await fetch(postAdditionalUserInfo({
                            annualIncomeMin,
                            annualIncomeMax,
                            liquidNetWorthMin,
                            liquidNetWorthMax,
                            fundingSource,
                            employmentStatus,
                            jobTitle,
                            employerName,
                            employerAddress,
                        }));
                        if (!userInfoResponse) {
                            throw new Error();
                        }
                    }
                }
                else {
                    throw new Error('Missing required onboarding data');
                }
            }
            if (fundingAccountToSet) {
                await dispatch(setConnectedAccount(fundingAccountToSet));
            }
            if (onboardingState?.fundingAccount ||
                onboardingState?.hasJISAOrTrustFund ||
                fundingAccountToSet ||
                hideFundingAccountAndAutoinvestFlow) {
                const bankAccount = onboardingState?.fundingAccount;
                const sortCode = bankAccount
                    ? ('ukSortCode' in bankAccount ? bankAccount.ukSortCode : bankAccount.sortCode).replace(/\D/g, '')
                    : '';
                const accountNumber = bankAccount
                    ? ('ukAccountNumber' in bankAccount ? bankAccount.ukAccountNumber : bankAccount.accountNumber).replace(/\D/g, '')
                    : '';
                const createTradingAccountParams = {
                    isAffiliatedExchangeOrFinra: false,
                    isControlPerson: false,
                    isPoliticallyExposed: false,
                    broker: 'seccl',
                    accountType,
                    ...(!!onboardingState.accountsHeldElsewhere?.length && {
                        accountsHeldElsewhere: onboardingState.accountsHeldElsewhere,
                    }),
                    ...(accountType === 'JISA' && {
                        jisaChildDetails: {
                            surname: onboardingState.lastName,
                            firstName: onboardingState.firstName,
                            dateOfBirth: format(onboardingState.dob, DOB_STORAGE_FORMAT),
                            hasPriorJisa: onboardingState.hasJISAOrTrustFund,
                            address: onboardingState.childAddress,
                        },
                    }),
                    ...(!hideFundingAccountAndAutoinvestFlow &&
                        (!onboardingState?.hasJISAOrTrustFund || accountType !== 'JISA') && {
                        postOpenDetails: {
                            directDebitMandate: {
                                sortCode,
                                accountNumber,
                            },
                            ...(onboardingState?.amount?.monthly &&
                                onboardingState?.assetId &&
                                onboardingState?.autoinvestDayOfMonth && {
                                autoInvest: {
                                    amount: onboardingState.amount.monthly,
                                    dayOfMonth: onboardingState.autoinvestDayOfMonth,
                                    allocations: [
                                        {
                                            assetId: onboardingState.assetId,
                                            percentage: 1,
                                        },
                                    ],
                                },
                            }),
                            ...(onboardingState?.assetId &&
                                onboardingState?.amount?.initial && {
                                singleDirectDebitInvest: {
                                    assetId: onboardingState.assetId,
                                    amount: onboardingState.amount.initial,
                                },
                            }),
                        },
                    }),
                    hasPostOpenInstructions: !hideFundingAccountAndAutoinvestFlow && (!onboardingState?.hasJISAOrTrustFund || accountType !== 'JISA'),
                };
                const response = await fetch(actions.createTradingAccount(createTradingAccountParams));
                if (response?.success) {
                    dispatch(postFunnelEvents([InvestFunnelEvents.ONB_INVEST_COMPLETED]));
                    if (accountType !== 'JISA') {
                        dispatch(actions.setAccountStatus('IDENTITY_CHECKS_IN_PROGRESS', broker, accountType));
                    }
                    dispatch(actions.updateWealthAccountsWithRetry(accountType === 'JISA' ? 10 : 17, broker, accountType));
                    dispatch(actions.clearOnboardingState());
                    LoadingAppViewModal.show({ isDone: true });
                    if (navigation.canGoBack()) {
                        navigation.popToTop();
                    }
                    LoadingAppViewModal.hide();
                }
                else {
                    throw new Error();
                }
            }
        }
        catch (e) {
            Sentry.captureException(e);
            LoadingAppViewModal.hide();
        }
    }, [annualIncome, employmentStatus, fundingSource, hasSourceOfFundsInfo, hideFundingAccountAndAutoinvestFlow, networth]);
    return {
        submit,
    };
};
